import AccountIcon from "@/components/AccountIcon";
import type {
  Award,
  Certification,
  CustomSection,
  CustomSectionGroup,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  SectionWithItem,
  Skill,
  Volunteer,
} from "@/lib/schema/sections";
import { TResume } from "@/lib/store/resume";
import { cn } from "@/lib/utils";
import { onPreviewSectionClick } from "@/lib/utils/resumeBuilder";
import { isUrl } from "@/utils";
import { isEmptyString } from "@/utils/preview";
import classNames from "classnames";
import { get } from "lodash";
import { Link as LinkIcon, Mail, MapPin, Phone } from "lucide-react";
import { Fragment } from "react";
import { TemplateProps } from ".";
import { LevelMap } from "../sidebars/left/sections/Languages";
import { Picture } from "../sidebars/left/sections/shared/Picture";

const Header = ({ resume }: { resume: TResume }) => {
  const basics = resume.data.basics;

  if (!basics) return null;
  const primaryColor = resume?.data.settings?.theme.primary;
  return (
    <div
      className="mb-4 flex flex-col items-center justify-between text-center"
      onClick={() =>
        onPreviewSectionClick(`builder-basics`, {
          highlight: false,
          accordion: false,
        })
      }
    >
      <Picture resume={resume} className="inline-block" />
      <div className="mt-1 text-center">
        <div className="space-y-2">
          <div className="text-resume-primary text-2xl font-bold">
            {basics.name}
          </div>
          <div className="text-xl text-gray-600">{basics.headline}</div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-custom text-sm text-gray-600">
          {basics.location && (
            <div className="flex items-center gap-x-1">
              <MapPin
                strokeWidth={3}
                size={14}
                className="resume-icon text-resume-primary"
              />
              <span>{basics.location}</span>
            </div>
          )}
          {basics.phone && (
            <div className="flex items-center gap-x-1">
              <Phone
                size={14}
                strokeWidth={3}
                className="resume-icon text-resume-primary"
              />
              <a href={`tel:${basics.phone}`}>{basics.phone}</a>
            </div>
          )}
          {basics.email && (
            <div className="flex items-center gap-x-1">
              <Mail
                size={14}
                strokeWidth={3}
                className="resume-icon text-resume-primary"
              />
              <a href={`mailto:${basics.email}`}>{basics.email}</a>
            </div>
          )}
          {basics.website?.href && (
            <div className="flex items-center gap-x-1">
              <LinkIcon
                size={14}
                strokeWidth={3}
                className="resume-icon text-resume-primary"
              />
              <a href={basics.website.href} target="_blank" rel="noreferrer">
                {basics.website.href}
              </a>
            </div>
          )}
          {basics.customFields?.map((item) => (
            <div key={item.id} className="flex items-center gap-x-1">
              <AccountIcon
                strokeWidth={3}
                name={item.icon}
                className="resume-icon text-resume-primary"
                size={14}
              />
              {item.name && (
                <span className="text-resume-primary font-bold">
                  {item.name}
                  {`: `}
                </span>
              )}
              {item.value && <span>{item.value}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Summary = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.summary;
  if (!section?.visible || isEmptyString(section?.content)) return null;
  const primaryColor = resume?.data.settings?.theme.primary;
  return (
    <section
      id={section?.id}
      className="section-item"
      onClick={() => onPreviewSectionClick(`builder-${section?.id}`)}
    >
      <div className="mb-2">
        <h4 className="flex items-center gap-3 text-lg font-bold">
          <span>{section?.name}</span>
          <div className="h-[1px] flex-1 bg-gray-300" />
        </h4>
      </div>

      <div dangerouslySetInnerHTML={{ __html: section?.content || "" }} />
    </section>
  );
};

type RatingProps = { level: number };

const Rating = ({ level }: RatingProps) => (
  <div className="flex items-center gap-x-1.5">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className={classNames(
          "border-resume-primary h-2 w-2 rounded-full border",
          level > index && "bg-resume-primary",
        )}
      />
    ))}
  </div>
);

type SectionProps<T> = {
  section?: SectionWithItem<T> | CustomSectionGroup;
  children?: (item: T) => React.ReactNode;
  className?: string;
  urlKey?: keyof T;
  levelKey?: keyof T;
  summaryKey?: keyof T;
  keywordsKey?: keyof T;
  resume: TResume;
};

const Section = <T,>({
  section,
  children,
  className,
  urlKey,
  levelKey,
  summaryKey,
  keywordsKey,
  resume,
}: SectionProps<T>) => {
  if (!section?.visible || !section?.items?.length) return null;
  const primaryColor = resume?.data.settings?.theme.primary;
  return (
    <section
      id={section.id}
      className="section-item grid"
      onClick={() =>
        onPreviewSectionClick(`builder-${section?.id}-${section?.items[0]?.id}`)
      }
    >
      <div className="mb-2">
        <h4 className="flex items-center gap-3 text-lg font-bold">
          <span>{section.name}</span>
          <div className="h-[1px] flex-1 bg-gray-300" />
        </h4>
      </div>

      <div
        className="grid gap-x-6 gap-y-3"
        style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
      >
        {section.items
          .filter((item) => item.visible)
          .map((item) => {
            const url = urlKey && get(item, urlKey);
            const level = LevelMap[levelKey && get(item, levelKey, "basic")];

            const summary = (summaryKey && get(item, summaryKey, "")) as
              | string
              | undefined;
            const keywords = (keywordsKey && get(item, keywordsKey, [])) as
              | string[]
              | undefined;
            return (
              <div
                key={item.id}
                className={classNames("space-y-2", className)}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewSectionClick(`builder-${section?.id}-${item.id}`);
                }}
              >
                {(children?.(item as T) || url?.href) && (
                  <div>
                    {children?.(item as T)}
                    {url?.href && (
                      <a target="_blank" rel="noreferrer" href={url?.href}>
                        {url?.href}
                      </a>
                    )}
                  </div>
                )}

                {summary !== undefined && !isEmptyString(summary) && (
                  <div dangerouslySetInnerHTML={{ __html: summary }} />
                )}

                {level !== undefined && level > 0 && <Rating level={level} />}

                {keywords !== undefined && keywords.length > 0 && (
                  <p className="text-sm">{keywords.join(", ")}</p>
                )}
              </div>
            );
          })}
      </div>
    </section>
  );
};

const Profiles = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.profiles;
  const fontSize = resume.data.settings?.typography.font.size;

  return (
    <Section<Profile> section={section} resume={resume}>
      {(item) => (
        <div>
          {isUrl(item.url?.href) ? (
            <a href={item.url?.href} className="text-resume-primary">
              <img
                className="resume-icon mr-1"
                width={fontSize}
                height={fontSize}
                alt={item.network}
                src={`https://cdn.simpleicons.org/${item.icon}`}
              />
              {item.username}
            </a>
          ) : (
            <div>{item.username}</div>
          )}
          {item.url?.href && (
            <a
              className="block text-sm"
              target="_blank"
              rel="noreferrer"
              href={item.url?.href}
            >
              {item.url?.href}
            </a>
          )}
        </div>
      )}
    </Section>
  );
};

const Experience = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.workExperience;
  return (
    <Section<Experience>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (!item.company && !item.position && !item.date && !item.location) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.company}</div>
              <div>{item.position}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
              <div>{item.location}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Internship = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.internship;
  return (
    <Section<Experience>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (!item.company && !item.position && !item.date && !item.location) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.company}</div>
              <div>{item.position}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
              <div>{item.location}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Education = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.education;
  return (
    <Section<Education> section={section} summaryKey="summary" resume={resume}>
      {(item) => {
        if (
          !item.institution &&
          !item.major &&
          !item.date &&
          !item.studyType &&
          !item.score &&
          !item.officialSite
        ) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.institution}</div>
              <div>{item.major}</div>
              <div>{item.score}</div>
              <div>{item.officialSite}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
              <div>{item.studyType}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Awards = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.awards;
  return (
    <Section<Award>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (!item.title && !item.awarder && !item.date) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.title}</div>
              <div>{item.awarder}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Certifications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.certifications;
  return (
    <Section<Certification>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (!item.name && !item.issuer && !item.date) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.name}</div>
              <div>{item.issuer}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Skills = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.skills;
  return (
    <Section<Skill>
      section={section}
      levelKey="level"
      keywordsKey="keywords"
      resume={resume}
    >
      {(item) => {
        if (!item.name && !item.description) {
          return null;
        }
        return (
          <div>
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.description}</div>
          </div>
        );
      }}
    </Section>
  );
};

const Interests = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.interests;
  return (
    <Section<Interest>
      resume={resume}
      section={section}
      keywordsKey="keywords"
      className="space-y-0.5"
    >
      {(item) => {
        if (!item.name) {
          return null;
        }
        return <div className="text-base font-bold">{item.name}</div>;
      }}
    </Section>
  );
};

const Publications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.publications;
  return (
    <Section<Publication>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (!item.name && !item.publisher && !item.date) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.name}</div>
              <div>{item.publisher}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Volunteer = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.volunteer;

  return (
    <Section<Volunteer>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (
          !item.organization &&
          !item.position &&
          !item.date &&
          !item.location
        ) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.organization}</div>
              <div>{item.position}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
              <div>{item.location}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const Languages = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.languages;

  return (
    <Section<Language> section={section} levelKey="level" resume={resume}>
      {(item) => {
        if (!item.name && !item.description) {
          return null;
        }
        return (
          <div className="space-y-0.5">
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.description}</div>
          </div>
        );
      }}
    </Section>
  );
};

const Projects = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.projects;

  return (
    <Section<Project>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
      resume={resume}
    >
      {(item) => {
        if (!item.name && !item.description && !item.date) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.name}</div>
              <div>{item.description}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const References = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.references;

  return (
    <Section<Reference>
      section={section}
      urlKey="url"
      summaryKey="summary"
      resume={resume}
    >
      {(item) => {
        if (!item.name && !item.description) {
          return null;
        }
        return (
          <div>
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.description}</div>
          </div>
        );
      }}
    </Section>
  );
};

const Custom = ({ id, resume }: { id: string; resume: TResume }) => {
  const section = resume.data.sections?.custom?.[id];
  return (
    <Section<CustomSection>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
      resume={resume}
    >
      {(item) => {
        if (!item.name && !item.description && !item.date && !item.location) {
          return null;
        }
        return (
          <div className={cn("flex items-center justify-between")}>
            <div className="text-left">
              <div className="text-base font-bold">{item.name}</div>
              <div>{item.description}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
              <div>{item.location}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const mapSectionToComponent = (section: any, resume: TResume) => {
  switch (section) {
    case "profiles":
      return <Profiles resume={resume} />;
    case "summary":
      return <Summary resume={resume} />;
    case "workExperience":
      return <Experience resume={resume} />;
    case "internship":
      return <Internship resume={resume} />;
    case "education":
      return <Education resume={resume} />;
    case "awards":
      return <Awards resume={resume} />;
    case "certifications":
      return <Certifications resume={resume} />;
    case "skills":
      return <Skills resume={resume} />;
    case "interests":
      return <Interests resume={resume} />;
    case "publications":
      return <Publications resume={resume} />;
    case "volunteer":
      return <Volunteer resume={resume} />;
    case "languages":
      return <Languages resume={resume} />;
    case "projects":
      return <Projects resume={resume} />;
    case "references":
      return <References resume={resume} />;
    default:
      if (section.startsWith("custom."))
        return <Custom id={section.split(".")[1]} resume={resume} />;

      return null;
  }
};

export const Reed = ({
  resume,
  columns,
  isFirstPage = false,
}: TemplateProps) => {
  const [main, sidebar] = columns;

  return (
    <div className="space-y-4 p-custom">
      {isFirstPage && <Header resume={resume} />}

      {main.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}

      {sidebar.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}
    </div>
  );
};
